import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
  MedicalInformation as MedicalInformationIcon,
  Pets as PetsIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { trimText } from "../../../../utils/Functions";

export default function TherapyOverview() {
  const methods = useFormContext();
  const { t } = useTranslation();
  const values = methods.getValues();

  return (
    <Box sx={{ mt: 2 }}>
      {values.alert && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          {t("PleaseReviewTherapy")}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: "background.default" }}>
        <Stack spacing={3}>
          <Header />
          <Divider />
          <PetInfo />
          <Divider />
          <DosageInfo />
          <Divider />
          {(values.dates?.length > 0 || values.numberOfDays) && <Dates />}
          {values.administration && <Administration />}
          {values.description && <Description />}
        </Stack>
      </Paper>
    </Box>
  );
}

function Description() {
  const methods = useFormContext();
  const { t } = useTranslation();
  const values = methods.getValues();
  return (
    <>
      <Divider />
      <Box>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InfoIcon fontSize="small" sx={{wordBreak:"break-word"}}/>
          {t("AdditionalInformation")}
        </Typography>
        <Typography variant="body1" sx={{wordBreak:"break-all"}}>
          {values.description}
        </Typography>
      </Box>
    </>
  );
}

function Administration() {
  const methods = useFormContext();
  const { t } = useTranslation();
  const values = methods.getValues();
  return (
    <>
      <Divider />
      <Box>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, wordBreak:"break-word"}} >
          {t("AdministrationInstructions")}
        </Typography>
        <Typography variant="body1" sx={{wordBreak:"break-all"}}>{values.administration}</Typography>
      </Box>
    </>
  );
}

function Dates() {
  const methods = useFormContext();
  const { t } = useTranslation();
  const values = methods.getValues();
  return (
    <Box>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CalendarTodayIcon fontSize="small" />
        {t("ScheduledDates")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {values?.dates.length > 0
          ? values?.dates?.map((date, index) => (
              <Chip
                key={index}
                label={date}
                variant="outlined"
                color="primary"
              />
            ))
          : getDates(values.numberOfDays)?.map((date, index) => (
              <Chip
                key={index}
                label={date}
                variant="outlined"
                color="primary"
              />
            ))}
      </Stack>
    </Box>
  );
}

function DosageInfo() {
  const methods = useFormContext();
  const { t } = useTranslation();
  const values = methods.getValues();
  return (
    <Box>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <AccessTimeIcon fontSize="small" />
        {t("DosageSchedule")}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("MorningDose")}
              </Typography>
              <Typography variant="h6" color="primary" sx={{wordBreak:"break-all"}}>
                {values.morningDoses || "-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("EveningDose")}
              </Typography>
              <Typography variant="h6" color="primary" sx={{wordBreak:"break-all"}}>
                {values.eveningDoses || "-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function PetInfo() {
  const methods = useFormContext();
  const { t } = useTranslation();
  const values = methods.getValues();
  return (
    <Box>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <PetsIcon fontSize="small" />
        {t("PetInformation")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            {t("Name")}
          </Typography>
          <Typography variant="body1" title={values.pet?.name}>
            {trimText(values.pet?.name, 45) || "Not specified"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            {t("Type")}
          </Typography>
          <Typography variant="body1">
            {values.pet?.type?.title || "Not specified"}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

function Header() {
  const methods = useFormContext();
  const { t } = useTranslation();
  const values = methods.getValues();
  return (
    <Box>
      <Stack
        direction={"row"}
        justifyContent={"flex-start"}
        alignItems={"center"}
        spacing={1}
        mb={2}
      >
        <MedicalInformationIcon />

        <Typography variant="h5">{t("TherapyOverview")}</Typography>
      </Stack>
      <Typography gutterBottom sx={{wordBreak:"break-all"}}> 
        {t("Title")}: {values.title || "Therapy Name"}
      </Typography>
      <Typography>
        {t("WillShowInAlertSection")}: {values.alert ? t("Yes") : t("No")}
      </Typography>
    </Box>
  );
}

function getDates(numberOfDays) {
  if (numberOfDays) {
    return Array.from({ length: numberOfDays }, (_, i) =>
      dayjs().add(i, "day").format("DD/MM/YYYY")
    );
  }
  return [];
}
